import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  RadioGroup, RadioGroupItem,
} from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const STATUS_COLOR = {
  active:   "border-l-emerald-500 text-emerald-300",
  paid:     "border-l-emerald-500 text-emerald-300",
  past_due: "border-l-[#FF3B30] text-[#FF3B30]",
  none:     "border-l-zinc-700 text-zinc-500",
};

const STATUS_LABEL = {
  active: "Active",
  paid: "Paid",
  past_due: "Past Due",
  none: "No Plan",
};

export default function BillingTab({ clientId, user }) {
  const isTrainer = user.role === "trainer";
  const [searchParams, setSearchParams] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [txns, setTxns] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/billing`);
      setPlan(data.plan); setTxns(data.transactions || []);
      setDraft(data.plan);
    } catch { toast.error("Failed to load billing"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  // Poll Stripe Checkout status if returning from Stripe with ?session_id=
  const sessionId = searchParams.get("session_id");
  const cancelled = searchParams.get("cancelled");
  useEffect(() => {
    if (cancelled) {
      toast.info("Payment cancelled.");
      setSearchParams((sp) => { sp.delete("cancelled"); return sp; }, { replace: true });
      return;
    }
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Payment successful — thank you!");
          setSearchParams((sp) => { sp.delete("session_id"); return sp; }, { replace: true });
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired.");
          setSearchParams((sp) => { sp.delete("session_id"); return sp; }, { replace: true });
          return;
        }
      } catch { /* ignore single failure */ }
      attempts += 1;
      if (attempts < 8) setTimeout(poll, 2000);
      else {
        toast.message("Still confirming payment — check back in a minute.");
        setSearchParams((sp) => { sp.delete("session_id"); return sp; }, { replace: true });
      }
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId, cancelled]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        type: draft.type || "none",
        amount: Number(draft.amount || 0),
        currency: draft.currency || "usd",
        description: draft.description || "",
        payment_link_url: draft.payment_link_url || "",
        paid_through: draft.paid_through || "",
        active: !!draft.active,
      };
      const { data } = await api.put(`/clients/${clientId}/billing`, body);
      setPlan(data); setDraft(data); setEditing(false);
      toast.success("Billing plan saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };

  const pay = async () => {
    setRedirecting(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/billing/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setRedirecting(false);
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
    }
  };

  if (!plan) return <div className="text-zinc-500 text-xs uppercase tracking-[0.3em]">Loading billing...</div>;

  const statusKey = plan.status || "none";
  const moneyFmt = new Intl.NumberFormat(undefined, { style: "currency", currency: (plan.currency || "usd").toUpperCase() });

  return (
    <div className="max-w-3xl space-y-8">
      {/* Status header */}
      <div className={`border border-zinc-800 border-l-2 ${STATUS_COLOR[statusKey] || STATUS_COLOR.none} p-6`}>
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">Billing Status</div>
            <div className="font-display text-4xl uppercase" data-testid="billing-status-label">{STATUS_LABEL[statusKey] || statusKey}</div>
            <div className="text-sm text-zinc-400 mt-1">{plan.description || (plan.type === "none" ? "No plan configured yet." : "—")}</div>
          </div>
          <div className="text-right">
            {plan.type !== "none" && plan.amount > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{plan.type === "subscription" ? "Monthly" : "Package"}</div>
                <div className="font-display text-3xl text-white">{moneyFmt.format(plan.amount)}</div>
              </div>
            )}
          </div>
        </div>
        {plan.type === "subscription" && plan.paid_through && (
          <div className="mt-4 text-xs uppercase tracking-[0.25em] text-zinc-400">
            Paid through <span className="text-white">{new Date(plan.paid_through).toLocaleDateString()}</span>
          </div>
        )}
        {plan.last_paid_at && (
          <div className="mt-2 text-xs uppercase tracking-[0.25em] text-zinc-400">
            Last paid {new Date(plan.last_paid_at).toLocaleString()}
          </div>
        )}
      </div>

      {/* Client-facing pay button */}
      {!isTrainer && plan.type !== "none" && plan.active && (
        <div className="border border-zinc-800 p-6">
          <div className="font-display text-2xl uppercase mb-2">Make a Payment</div>
          <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mb-5">
            {plan.type === "subscription"
              ? "Opens a secure Stripe page for your monthly coaching subscription."
              : "Opens a secure Stripe checkout for your package."}
          </div>
          <Button data-testid="billing-pay-btn" onClick={pay} disabled={redirecting} className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-white uppercase tracking-[0.2em] h-12 px-8 text-xs">
            {redirecting ? "Redirecting..." : plan.type === "subscription" ? "Manage Subscription" : `Pay ${moneyFmt.format(plan.amount)}`}
          </Button>
        </div>
      )}

      {/* Trainer editor */}
      {isTrainer && (
        <div className="border border-zinc-800 p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="font-display text-2xl uppercase">Plan Settings</div>
            {!editing ? (
              <Button data-testid="billing-edit-btn" onClick={() => { setDraft(plan); setEditing(true); }} className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-xs uppercase tracking-[0.2em] h-10">
                {plan.type === "none" ? "Configure Plan" : "Edit"}
              </Button>
            ) : null}
          </div>

          {editing ? (
            <div className="space-y-5">
              <RadioGroup value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })} className="grid sm:grid-cols-3 gap-2">
                {[
                  ["subscription", "Monthly Subscription"],
                  ["one_time", "One-Time Package"],
                  ["none", "No Plan"],
                ].map(([v, l]) => (
                  <Label key={v} htmlFor={`bp-${v}`} className={`flex items-center gap-2 border p-3 cursor-pointer ${draft.type === v ? "border-[#FF3B30] bg-[#1A1A1A]" : "border-zinc-800 hover:border-zinc-700"}`}>
                    <RadioGroupItem value={v} id={`bp-${v}`} data-testid={`billing-type-${v}`} className="border-zinc-600" />
                    <span className="text-xs uppercase tracking-[0.2em]">{l}</span>
                  </Label>
                ))}
              </RadioGroup>

              {draft.type !== "none" && (
                <>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <FieldLabel label="Amount (USD)">
                      <Input data-testid="billing-amount" type="number" min="0" step="1" value={draft.amount || ""} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-11 font-mono" />
                    </FieldLabel>
                    <FieldLabel label="Description">
                      <Input data-testid="billing-description" value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="e.g. Monthly Coaching" className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-11" />
                    </FieldLabel>
                  </div>
                  {draft.type === "subscription" && (
                    <>
                      <FieldLabel label="Stripe Payment Link URL" hint="Open dashboard.stripe.com → Payment Links → copy the link. Clients will be redirected here when they tap 'Manage Subscription'.">
                        <Input data-testid="billing-link" value={draft.payment_link_url || ""} onChange={(e) => setDraft({ ...draft, payment_link_url: e.target.value })} placeholder="https://buy.stripe.com/..." className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-11" />
                      </FieldLabel>
                      <FieldLabel label="Paid Through (date)" hint="Manually set after each successful month — used to show 'Active' / 'Past Due'.">
                        <Input data-testid="billing-paid-through" type="date" value={draft.paid_through || ""} onChange={(e) => setDraft({ ...draft, paid_through: e.target.value })} className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-11 w-48" />
                      </FieldLabel>
                    </>
                  )}
                </>
              )}

              <div className="flex gap-2 pt-2">
                <Button data-testid="billing-save" onClick={save} disabled={saving} className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-xs uppercase tracking-[0.2em] h-11 px-6">{saving ? "Saving..." : "Save"}</Button>
                <Button onClick={() => { setEditing(false); setDraft(plan); }} variant="outline" className="rounded-none border-zinc-800 bg-transparent text-xs uppercase tracking-[0.2em] hover:bg-zinc-900 hover:text-white">Cancel</Button>
              </div>
            </div>
          ) : plan.type === "none" ? (
            <div className="text-zinc-500 text-sm">No plan configured yet. Click <span className="text-white">Configure Plan</span>.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Field k="Type" v={plan.type === "subscription" ? "Monthly Subscription" : "One-Time Package"} />
              <Field k="Amount" v={moneyFmt.format(plan.amount || 0)} />
              <Field k="Description" v={plan.description || "—"} />
              {plan.type === "subscription" && <>
                <Field k="Payment Link" v={plan.payment_link_url ? <a className="text-[#FF3B30] underline break-all" href={plan.payment_link_url} target="_blank" rel="noreferrer">{plan.payment_link_url}</a> : "—"} />
                <Field k="Paid Through" v={plan.paid_through ? new Date(plan.paid_through).toLocaleDateString() : "—"} />
              </>}
            </div>
          )}
        </div>
      )}

      {/* Transactions */}
      {txns.length > 0 && (
        <div>
          <div className="font-display text-2xl uppercase mb-3">Transaction History</div>
          <div className="border border-zinc-800">
            <div className="grid grid-cols-5 bg-[#121212] text-[10px] uppercase tracking-[0.25em] text-zinc-500 px-4 py-3">
              <div>Date</div><div>Amount</div><div>Status</div><div>Payment</div><div>Session</div>
            </div>
            {txns.map((t, i) => (
              <div key={t.id} className={`grid grid-cols-5 px-4 py-3 text-sm ${i % 2 ? "bg-[#0A0A0A]" : "bg-[#121212]"}`}>
                <div className="font-mono text-zinc-300 text-xs">{new Date(t.created_at).toLocaleString()}</div>
                <div className="font-mono">{moneyFmt.format(t.amount || 0)}</div>
                <div className={`text-xs uppercase tracking-[0.2em] ${t.payment_status === "paid" ? "text-emerald-400" : t.status === "expired" ? "text-zinc-500" : "text-yellow-300"}`}>{t.status}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-300">{t.payment_status || "—"}</div>
                <div className="font-mono text-[10px] text-zinc-600 truncate">{t.session_id}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const FieldLabel = ({ label, hint, children }) => (
  <div>
    <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">{label}</label>
    {children}
    {hint && <div className="text-[11px] text-zinc-600 mt-1.5 leading-relaxed">{hint}</div>}
  </div>
);

const Field = ({ k, v }) => (
  <div>
    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">{k}</div>
    <div className="text-zinc-200">{v}</div>
  </div>
);
